import styles from './css/Registration.css';

import React from 'react';
import { Link } from 'react-router';
import classNames from 'classnames';
import { Button, Checkbox, Dropdown, Icon, TextInput } from '@tcc/crate';
import { getMessage } from '../../i18n/i18nWrapper';
import InfoColumn from './InfoColumn';
import { getSortedCountryList } from '../../constants/countryList';
import PropTypes from 'prop-types';

export const Registration = ({
  apiErrorText,
  country,
  createAccount,
  email,
  emailErrorText,
  eulaErrorText,
  eulaLink,
  firstName,
  firstNameErrorText,
  isEulaAccepted,
  isRegistrationFetching,
  isShowingPassword,
  lastName,
  lastNameErrorText,
  password,
  passwordErrorText,
  phoneNumber,
  phoneNumberErrorText,
  ppLink,
  setCountry,
  setEmail,
  setFirstName,
  setLastName,
  setPassword,
  setPhoneNumber,
  toggleEulaAcceptance,
  toggleShowPassword
}) => {

  function formCreateAccount(e) {
    e.preventDefault();
    createAccount();
  }

  const acceptEulaLabel = (
    <div>
      {getMessage('registration.agreeEula')}&nbsp;
      <a data-test-id="eula-link" href={eulaLink} target="_blank">
        {getMessage('registration.eula')}
      </a>
      , {getMessage('registration.includes')}&nbsp;
      <a data-test-id="privacy-policy-link" href={ppLink} target="_blank">
        {getMessage('registration.privacyPolicy')}
      </a>
    </div>
  );

  const showPasswordStyles = classNames(styles.showPasswordText, {
    [styles.showPasswordError]: passwordErrorText
  });

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logoContainer} data-test-id="climate-fv-logo">
          <Icon className={styles.logo} glyph={Icon.set.CLIMATE_PORTAL_COLOR} />
        </div>

        <div className={styles.columns}>
          <div className={styles.leftColumn}>

            <p className={styles.title}>{getMessage('registration.title')}</p>
            <form className={styles.registrationForm} onSubmit={formCreateAccount}>
              <TextInput
                error={getMessage(`registration.error.${firstNameErrorText || apiErrorText}`)}
                label={getMessage('registration.firstName')}
                name="firstName"
                onChange={setFirstName}
                testId="first-name"
                value={firstName} />

              <TextInput
                error={getMessage(`registration.error.${lastNameErrorText}`)}
                label={getMessage('registration.lastName')}
                name="lastName"
                onChange={setLastName}
                testId="last-name"
                value={lastName} />

              <TextInput
                autoCapitalize="none"
                autoCorrect="off"
                error={getMessage(`registration.error.${emailErrorText}`)}
                label={getMessage('registration.email')}
                name="email"
                onChange={setEmail}
                testId="email"
                value={email} />

              <div className={styles.passwordInputContainer}>
                <TextInput
                  className={styles.passwordTextInput}
                  error={getMessage(`registration.error.${passwordErrorText}`)}
                  label={getMessage('registration.password')}
                  name="password"
                  onChange={setPassword}
                  testId="password"
                  type={isShowingPassword ? 'text' : 'password'}
                  value={password}>

                  <span className={showPasswordStyles} onClick={toggleShowPassword}>
                    {isShowingPassword
                      ? getMessage('registration.hide')
                      : getMessage('registration.show')
                    }
                  </span>

                </TextInput>
              </div>

              <TextInput
                error={getMessage(`registration.error.${phoneNumberErrorText}`)}
                label={getMessage('registration.phone')}
                name="phoneNumber"
                onChange={setPhoneNumber}
                testId="phone"
                value={phoneNumber} />

              <Dropdown
                className={styles.countryDropdown}
                label={getMessage('registration.country')}
                options={getSortedCountryList()}
                searchable
                setValue={setCountry}
                testId="country"
                value={country} />

              <div className={styles.checkboxContainer}>
                <Checkbox
                  checked={isEulaAccepted}
                  className={styles.checkbox}
                  label={acceptEulaLabel}
                  onChange={toggleEulaAcceptance}
                  testId="accept-eula"
                />
                <span
                  className={styles.checkboxError}
                  data-test-id="eula-error-label"
                >
                  {getMessage(`registration.error.${eulaErrorText}`)}
                </span>
              </div>

              <Button
                className={styles.button}
                isLoading={isRegistrationFetching}
                mode="primary"
                testId="create-account"
                type="submit">
                {getMessage('registration.createAccount')}
              </Button>
            </form>

            <Link
              className={styles.link}
              data-test-id="back-to-login"
              href={`${window.location.pathname}#/`}
            >
              {getMessage('registration.haveAccount')}
            </Link>

            <div className={styles.borderContainer}>
              <div className={styles.rightBorder} />
            </div>
          </div>

          <InfoColumn />

        </div>
      </div>
    </div>
  );
};

Registration.propTypes = {
  // Text describing any unexpected API errors
  apiErrorText: PropTypes.string.isRequired,

  // User's selected country
  country: PropTypes.string,

  // Request registration / account creation API to be called
  createAccount: PropTypes.func.isRequired,

  // User's inputed email
  email: PropTypes.string.isRequired,

  // Text describing any email-specific errors
  emailErrorText: PropTypes.string.isRequired,

  // Text describing any EULA errors
  eulaErrorText: PropTypes.string.isRequired,

  // URL for EULA of currently selected country
  eulaLink: PropTypes.string.isRequired,

  // User's inputed first name
  firstName: PropTypes.string.isRequired,

  // Text describing any firstName-specific errors
  firstNameErrorText: PropTypes.string.isRequired,

  // Boolean defining whether the user accepted the EULA or not
  isEulaAccepted: PropTypes.bool.isRequired,

  // Boolean that determines if the request to register is in progress
  isRegistrationFetching: PropTypes.bool.isRequired,

  // Boolean that determines whether to show or hide the user's password
  isShowingPassword: PropTypes.bool.isRequired,

  // User's inputed last name
  lastName: PropTypes.string.isRequired,

  // Text describing any lastName-specific errors
  lastNameErrorText: PropTypes.string.isRequired,

  // User's inputed password
  password: PropTypes.string.isRequired,

  // Text describing any password-specific errors
  passwordErrorText: PropTypes.string.isRequired,

  // User's inputed phone number
  phoneNumber: PropTypes.string.isRequired,

  // Text describing any phone number-specific errors
  phoneNumberErrorText: PropTypes.string.isRequired,

  // URL for the Privacy Policy of currently selected country
  ppLink: PropTypes.string.isRequired,

  // Click handler that sets the user's country
  setCountry: PropTypes.func.isRequired,

  // Emails's textinput's onChange function
  setEmail: PropTypes.func.isRequired,

  // First name's textinput's onChange function
  setFirstName: PropTypes.func.isRequired,

  // Last name's textinput's onChange function
  setLastName: PropTypes.func.isRequired,

  // Password's textinput's onChange function
  setPassword: PropTypes.func.isRequired,

  // Phone number's textinput's onChange function
  setPhoneNumber: PropTypes.func.isRequired,

  // Click handler that toggles the isEulaAccepted boolean
  toggleEulaAcceptance: PropTypes.func.isRequired,

  // Click handler that toggles the isShowingPassword boolean
  toggleShowPassword: PropTypes.func.isRequired
};

export default Registration;
